import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Alert, Button, Card, Container, Spinner } from "react-bootstrap";
import API_BASE_URL from "../config/apiConfig";
import { AcceptanceProofFields, useAcceptanceProof } from "../components/AcceptanceProof";

const CONFIRMATION_API = `${API_BASE_URL}/api/public/hotel-booking-confirmations`;

// Acceptance times are stored in UTC without a zone marker, so mark them before showing local time.
const formatAcceptedAt = (value) => {
  if (!value) return "";
  const date = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(String(value)) ? value : `${value}Z`);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" });
};

const BookingDetails = ({ confirmation }) => (
  <div className="text-start bg-light rounded p-3" style={{ color: "#0f172a" }}>
    <div><strong>Booking ID:</strong> #{confirmation?.bookingId || "-"}</div>
    <div><strong>Hotel:</strong> {confirmation?.hotelName || "Hotel"}</div>
    <div><strong>Date:</strong> {confirmation?.dateLabel || "-"}</div>
    {confirmation?.roomSummary && (
      <div className="mt-2">
        <strong>Rooms:</strong>
        <div style={{ whiteSpace: "pre-line" }}>{confirmation.roomSummary}</div>
      </div>
    )}
  </div>
);

/**
 * The page behind the acceptance link we send hotels on WhatsApp. Opening it only shows the
 * booking; it is accepted when the hotel gives a name and location and presses Accept.
 */
export default function HotelBookingConfirmationPage() {
  const { token } = useParams();
  // loading | pending | accepted | error
  const [status, setStatus] = useState("loading");
  const [confirmation, setConfirmation] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const proofState = useAcceptanceProof(status === "pending");

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    axios.get(`${CONFIRMATION_API}/${encodeURIComponent(token || "")}/details`)
      .then((response) => {
        if (cancelled) return;
        setConfirmation(response.data || null);
        setStatus(response.data?.accepted ? "accepted" : "pending");
      })
      .catch(() => {
        if (!cancelled) setStatus("error");
      });
    return () => { cancelled = true; };
  }, [token]);

  const accept = async () => {
    if (!proofState.proof) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      const { data } = await axios.post(
        `${CONFIRMATION_API}/${encodeURIComponent(token || "")}/accept`,
        proofState.proof,
      );
      proofState.markUsed();
      setConfirmation(data || confirmation);
      setStatus("accepted");
    } catch (error) {
      setSubmitError(error?.response?.data?.message || "Could not accept the booking. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container className="py-5" style={{ maxWidth: 720 }}>
      <Card className="shadow-sm border-0">
        <Card.Body className="p-4 p-md-5">
          {status === "loading" && (
            <div className="text-center">
              <Spinner className="mb-3" />
              <h3>Loading booking...</h3>
            </div>
          )}

          {status === "pending" && (
            <>
              <h2 className="mb-1">Accept this booking</h2>
              <p className="mb-3" style={{ color: "#475569" }}>Please check the booking below.</p>
              <BookingDetails confirmation={confirmation} />
              <Alert variant="warning" className="small mt-3">
                Once accepted, this booking cannot be denied in future. Please keep these rooms for this guest.
              </Alert>
              <AcceptanceProofFields state={proofState} disabled={submitting} />
              {submitError && <Alert variant="danger" className="small mt-3 mb-0">{submitError}</Alert>}
              <Button
                variant="success"
                size="lg"
                className="w-100 mt-3"
                disabled={submitting || !proofState.proof}
                onClick={accept}
              >
                {submitting ? <Spinner animation="border" size="sm" /> : "Accept booking"}
              </Button>
            </>
          )}

          {status === "accepted" && (
            <div className="text-center">
              <div className="mx-auto mb-3 d-flex align-items-center justify-content-center rounded-circle bg-success text-white" style={{ width: 84, height: 84, fontSize: 48 }}>
                &#10003;
              </div>
              <h2 className="mb-2">Thanks for accepting the booking</h2>
              <p className="mb-4" style={{ color: "#475569" }}>
                {confirmation?.acceptedByName
                  ? `Accepted by ${confirmation.acceptedByName}${confirmation?.acceptedAt ? ` on ${formatAcceptedAt(confirmation.acceptedAt)}` : ""}.`
                  : "Booking accepted successfully."}
              </p>
              <BookingDetails confirmation={confirmation} />
            </div>
          )}

          {status === "error" && (
            <Alert variant="danger" className="mb-0">
              We could not find this booking. Please check the link or contact IO Trips.
            </Alert>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}
